import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import { IoIosCloseCircle } from 'react-icons/io';
import { FaEdit, FaSave, FaTruck } from 'react-icons/fa';
import { useMyContext } from '../../../context/MyContext'
import Spinner from '../../spinner/spinner'
import { Base_Url, ndrAction } from '../../../config/config';

// Form validation schema

const schema = z.object({
  id: z.any().optional(),
  user_id: z.any().optional(),
  waybill: z.string().optional(),
  order_id: z.string().min(1, 'Order ID is required'),
  count: z.any().optional(),
  order_partner: z.string().optional(),
  updated_at: z.string().optional(),
  order_drop_state: z.string().optional(),
  order_drop_city: z.string().optional(),
  order_phone: z.string().min(10, 'Phone no must be at least 10 digits'),
  order_drop_pin: z.string().optional(),
  order_drop_address: z.string().min(1, 'Address is required'),
  order_first_name: z.string().optional(),
  order_last_name: z.string().optional(),
  reason: z.string().optional(),
  action: z.string().optional(),
  response: z.string().optional(),
  phone: z.string().optional()
});

const NdrModel = ({ isopen, onClose, ndrdata }) => {
  const token = localStorage.getItem('token');
  const cusid = localStorage.getItem('cus_id');
  const { isDarkMode } = useMyContext();
  const [isEditMode, setIsEditMode] = useState(true);
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue, reset, getValues } = useForm({
    resolver: zodResolver(schema),
  });

  // Initialize form with ndrdata when it changes
  useEffect(() => {
    if (ndrdata) {
      const partnerValue = ndrdata.order_partner || ndrdata.partner || '';
      const phoneValue = ndrdata.order_phone || ndrdata.phone || '';
      const addressValue = ndrdata.order_drop_address || ndrdata.address || '';
      
      const merged = {
        ...ndrdata,
        order_partner: partnerValue,
        order_phone: phoneValue,
        order_drop_address: addressValue,
      };

      Object.keys(merged).forEach(key => {
        const val = merged[key];
        setValue(key, val !== null && val !== undefined ? String(val) : '');
      });
    }
  }, [ndrdata, setValue]);


  const onErrors = (errors) => {
    toast.error("Please fix the errors in the form.");
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const editNdrEndpoint = Base_Url + ndrAction; // Replace with your actual endpoint
    // const phoneValue = getValues('order_phone');
    const payload = {
      order_id: data.order_id,
      address: data.order_drop_address,
      phone: data.order_phone,
      action: "Re-attempt"
    };

    try {
      const response = await axios.post(editNdrEndpoint, payload, {
        headers: {
          'token': token,
          'cus_id': cusid,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.status === "SUCCESS") {
        toast.success(response.data.message || 'NDR data updated successfully');
        handleClose();
      } else {
        toast.error(response.data.message || 'Failed to update NDR data');
      }
    } catch (error) {
      const errorData = error.response?.data;
      // Handle detailed validation errors
      if (errorData?.messages && typeof errorData.messages === 'object') {
        const detailedErrors = Object.values(errorData.messages).join(', ');
        toast.error(detailedErrors || 'Validation error occurred');
      } else {
        toast.error(errorData?.message || 'An error occurred while updating NDR data');
      }
    }
    finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // setIsEditMode(false);
    reset();
    onClose();
  };


  // Reusable Input Field Component
  const InputField = ({ label, name, disabled = true, type = "text", maxLength }) => (
    <div className={`p-4 rounded-lg shadow-sm transition-all duration-200 ${disabled
      ? 'bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
      : 'bg-white dark:bg-gray-800 border-2 border-blue-300 dark:border-blue-500 shadow-md'
      }`}>
      <label className={`block text-sm font-bold mb-2 flex items-center ${disabled
        ? 'text-gray-500 dark:text-gray-400'
        : 'text-gray-700 dark:text-gray-300'
        }`}>
        {!disabled && (
          <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
        )}
        {label}
        {!disabled && (
          <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
            EDITABLE
          </span>
        )}
      </label>
      <input
        type={type}
        className={`mt-1 p-3 w-full rounded-lg transition-all duration-200 ${disabled
          ? // Disabled styles
          'border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-70'
          : // Enabled styles
          'border-2 border-blue-400 dark:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-600 dark:focus:border-blue-400 hover:border-blue-500 dark:hover:border-blue-400 shadow-sm hover:shadow-md'
          }`}
        {...register(name)}
        disabled={disabled}
        maxLength={maxLength}
      />
      {errors[name] && <p className="text-red-500 text-sm mt-1">{errors[name].message}</p>}
      {!disabled && (
        <div className="flex items-center text-xs text-blue-600 dark:text-blue-400 mt-1">
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
          This field can be edited
        </div>
      )}
    </div>
  );

  return (
    <Dialog
      open={isopen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        className: "rounded-lg overflow-hidden"
      }}
    >
      <DialogTitle className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white relative shadow-lg">
        <h2 className="text-xl sm:text-2xl font-bold text-white font-sans px-2 py-3 flex items-center">
          <FaTruck className="mr-3" size={26} />
          NDR Details
        </h2>
        <IoIosCloseCircle
          className="absolute top-3 right-3 cursor-pointer hover:scale-110 transition-transform"
          size={32}
          onClick={handleClose}
        />
      </DialogTitle>

      <DialogContent className="bg-gray-50 dark:bg-gray-900 custom-scrollbar" style={{ maxHeight: '75vh' }}>
        <div className="p-6 bg-white dark:bg-gray-800 shadow-md rounded-lg dark:text-gray-200">
          <form className="space-y-8 font-sans" onSubmit={handleSubmit(onSubmit, onErrors)}>

            {/* Shipping Information Section */}
            <div className="bg-purple-50 dark:bg-gray-800 p-4 rounded-lg border-l-4 border-purple-500">
              <h3 className="text-lg font-bold mb-4 text-purple-700 dark:text-purple-300 flex items-center">
                <FaTruck className="mr-2" />
                Shipping Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField label="ID" name="id" disabled={true} />
                <InputField label="User ID" name="user_id" disabled={true} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <InputField label="Waybill" name="waybill" disabled={true} />
                <InputField label="Order ID" name="order_id" disabled={true} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <InputField label="Partner" name="order_partner" disabled={true} />
                <InputField label="Timestamp" name="updated_at" disabled={true} />
              </div>
            </div>

            {/* Customer Information Section */}
            <div className="bg-blue-50 dark:bg-gray-800 p-4 rounded-lg border-l-4 border-blue-500">
              <h3 className="text-lg font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
                <FaEdit className="mr-2" />
                Customer Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField label="First Name" name="order_first_name" disabled={true} />
                <InputField label="Last Name" name="order_last_name" disabled={true} />
              </div>
            </div>

            {/* Address Information Section */}
            <div className="bg-green-50 dark:bg-gray-800 p-4 rounded-lg border-l-4 border-green-500">
              <h3 className="text-lg font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
                <FaEdit className="mr-2" />
                Address Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField label="Address" name="order_drop_address" disabled={!isEditMode} />
                <InputField label="Phone Number" name="order_phone" disabled={!isEditMode} maxLength={10} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                <InputField label="City" name="order_drop_city" disabled={true} />
                <InputField label="State" name="order_drop_state" disabled={true} />
                <InputField label="Pincode" name="order_drop_pin" disabled={true} maxLength={6} />
              </div>
            </div>

            {/* NDR Information Section */}
            {/* <div className="bg-yellow-50 dark:bg-gray-800 p-4 rounded-lg border-l-4 border-yellow-500">
              <h3 className="text-lg font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
                <FaExclamationTriangle className="mr-2" />
                NDR Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputField label="Reason" name="reason" disabled={true} />
                <InputField label="Action" name="action" disabled={true} />
              </div>

              <div className="grid grid-cols-1 gap-6 mt-4">
                <InputField label="Response" name="response" disabled={true} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <InputField label="Count" name="count" disabled={true} />
              </div>
            </div> */}

            {/* Action Buttons */}
            <div className="flex space-x-4 mt-8">
              {/* <button
                type="button"
                onClick={toggleEditMode}
                className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white py-3 px-6 rounded-lg hover:from-purple-700 hover:to-indigo-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 font-medium transition-colors flex items-center shadow-md"
              >
                {isEditMode ? (
                  <>
                    <FaTimes className="mr-2" /> Cancel Editing
                  </>
                ) : (
                  <>
                    <FaEdit className="mr-2" /> Edit NDR Data
                  </>
                )}
              </button> */}

              {isEditMode && (
                <button
                  type="submit"
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-lg hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 font-medium transition-colors flex items-center shadow-md"
                >
                  <FaSave className="mr-2" />
                  {loading ? <Spinner /> : "Save Changes"}

                </button>
              )}
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};


export default NdrModel;